import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnchor, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getApiEndpoint } from '../../../../config/api';

const initialFormData = {
  name: '',
  location_description: '',
  type: 'Fall Arrest',
  last_inspection_date: new Date().toISOString().split('T')[0],
  next_inspection_date: '',
  notes: ''
};

function addMonths(date, months) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() < day) d.setDate(0);
  return d;
}

function calculateNextTestDate(type, lastDate) {
  if (!lastDate) return '';
  if (type === 'Rope Access') {
    return addMonths(lastDate, 6).toISOString().split('T')[0];
  }
  const d = new Date(lastDate);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split('T')[0];
}

const CreateAnchorPointModal = ({ isOpen, onClose, onSuccess, propertyId }) => {
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        ...initialFormData,
        last_inspection_date: today,
        next_inspection_date: calculateNextTestDate('Fall Arrest', today)
      });
    }
  }, [isOpen]);

  useEffect(() => {
    setFormData(f => ({
      ...f,
      next_inspection_date: calculateNextTestDate(f.type, f.last_inspection_date)
    }));
  }, [formData.type, formData.last_inspection_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/anchor-points`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create anchor point');
      }

      const newAnchorPoint = await response.json();
      toast.success('Anchor point created successfully');
      onSuccess(newAnchorPoint);
      onClose();
    } catch (error) {
      console.error('Error creating anchor point:', error);
      toast.error(error.message || 'Failed to create anchor point');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rs-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rs-modal" role="dialog" aria-modal="true" aria-labelledby="create-ap-title">
        <div className="rs-modal-header">
          <h2 id="create-ap-title">
            <FontAwesomeIcon icon={faAnchor} style={{ marginRight: 8, color: 'var(--cs-blue, #1A80E5)' }} />
            Create Anchor Point
          </h2>
          <button className="rs-modal-close" onClick={onClose} aria-label="Close modal">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="rs-modal-body">
            <div className="rs-form-grid">
              <div className="rs-form-group">
                <label htmlFor="create-ap-name">ID *</label>
                <input
                  id="create-ap-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter anchor point ID"
                />
              </div>

              <div className="rs-form-group">
                <label htmlFor="create-ap-type">Type *</label>
                <select
                  id="create-ap-type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="Rope Access">Rope Access</option>
                  <option value="Fall Arrest">Fall Arrest</option>
                  <option value="Restraint">Restraint</option>
                </select>
              </div>

              <div className="rs-form-group rs-full-width">
                <label htmlFor="create-ap-location">Location Description *</label>
                <input
                  id="create-ap-location"
                  type="text"
                  value={formData.location_description}
                  onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                  required
                  placeholder="e.g., Roof Zone A, North Side"
                />
              </div>

              <div className="rs-form-group">
                <label htmlFor="create-ap-last-date">Last Inspection Date *</label>
                <input
                  id="create-ap-last-date"
                  type="date"
                  value={formData.last_inspection_date}
                  onChange={(e) => setFormData({ ...formData, last_inspection_date: e.target.value })}
                  required
                />
              </div>

              <div className="rs-form-group">
                <label htmlFor="create-ap-next-date">Next Test Date</label>
                <input
                  id="create-ap-next-date"
                  type="date"
                  value={formData.next_inspection_date}
                  readOnly
                />
              </div>

              <div className="rs-form-group rs-full-width">
                <label htmlFor="create-ap-notes">Notes</label>
                <textarea
                  id="create-ap-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Brief description of the anchor point"
                  rows="3"
                />
              </div>
            </div>
          </div>

          <div className="rs-modal-footer">
            <button type="button" className="rs-btn rs-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="rs-btn rs-btn-primary">
              <FontAwesomeIcon icon={faAnchor} /> Create Anchor Point
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAnchorPointModal;
